import { connectWithRouter } from 'helpers/connectWithRouter';
import Paper from '@mui/material/Paper';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TravelerTableRow from './TravelerTableRow';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TableCell from '@mui/material/TableCell';
import IconButton from '@mui/material/IconButton';
import Tooltip from '@mui/material/Tooltip';
import SyncIcon from '@mui/icons-material/Sync';
import CircularProgress from '@mui/material/CircularProgress';
import { bindActionCreators, Dispatch } from 'redux';
import { ReduxState } from '../../reducers/types';
import moment from 'moment';
import * as _ from 'lodash-es';
import UpdateFlightCreditPopup from 'components/Popup/UpdateFlightCreditPopup';
import { updateUserFlightCredit } from 'actions/User/UpdateUserFlightCredit';
import { syncUserFlightCredit } from 'actions/User/SyncUserFlightCredit';

const styles = require('./styles.pcss');
import * as React from 'react';

type Props = {
  hasPermission: any;
  isLoading: any;
  userLoading: any;
  flightCredits: any;
  updateUserFlightCredit: any;
  syncUserFlightCredit: (id: string) => void;
  isSyncingFlightCredit: boolean;
};

type State = {
  flightCredits: any;
  ticketNumber: string;
  status: string;
  updatePopup: boolean;
  id: string;
  syncingTicketId: string | null;
};

class FlightCredits extends React.Component<Props, State> {
  constructor(props: Props) {
    super(props);

    this.state = {
      updatePopup: false,
      ticketNumber: '',
      status: '',
      id: '',
      flightCredits: this.props.flightCredits,
      syncingTicketId: null,
    };
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.flightCredits !== this.props.flightCredits) {
      this.setState({ flightCredits: this.props.flightCredits });
    }

    if (
      prevProps.isSyncingFlightCredit &&
      !this.props.isSyncingFlightCredit &&
      this.state.syncingTicketId !== null
    ) {
      this.setState({ syncingTicketId: null });
    }
  }

  handleUpdateStatus = (flightCreditId: any) => {
    const changedFlightCreditIndex = this.state.flightCredits.findIndex(
      (cred: any) => cred.id === flightCreditId,
    );
    const flightCredits = [...this.state.flightCredits];
    const flightCredit = { ...flightCredits[changedFlightCreditIndex] };
    flightCredit.status = this.state.status;
    flightCredits[changedFlightCreditIndex] = flightCredit;
    this.setState({ flightCredits });
    this.props.updateUserFlightCredit(flightCreditId, this.state.status);
  };

  handleSync = (id: string) => {
    this.setState({ syncingTicketId: id });
    this.props.syncUserFlightCredit(id);
  };

  handleStatusSelect = (status: string) => {
    this.setState({ status });
  };

  onClickEdit = (ticketNumber: any, status: any, id: any) => {
    this.setState({
      id,
      ticketNumber,
      status,
      updatePopup: true,
    });
  };

  onClosePopup = () => {
    this.setState({
      updatePopup: false,
    });
  };

  render() {
    return (
      <Paper className={styles.paper}>
        <div className={styles.iconAndInfoContainer}>
          <div className={styles.iconContainer}></div>
          <div className={styles.infoContainer}>
            <div className={styles.titleStyle}>Flight Credits</div>
            <div className={styles.subtitleStyle}>
              An overview of the traveler’s flight credits. Flight credits may
              take up to 24 hours to update.
            </div>
            <div className={styles.flightCreditsTableWrapper}>
              <Table className={styles.loyaltyProgramTable}>
                <TableHead>
                  <TableRow>
                    <TableCell className={styles.flightCreditsHead}>
                      Airline
                    </TableCell>
                    <TableCell className={styles.flightCreditsHead}>
                      Traveler Name
                    </TableCell>
                    <TableCell className={styles.flightCreditsHead}>
                      Ticket Number
                    </TableCell>
                    <TableCell className={styles.flightCreditsHead}>
                      Value
                    </TableCell>
                    <TableCell className={styles.flightCreditsHead}>
                      Valid Until
                    </TableCell>
                    <TableCell className={styles.flightCreditsHead}>
                      Status
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {this.state.flightCredits.map((flightCredit: any) => {
                    return (
                      <TravelerTableRow
                        firstItemToLeft={true}
                        key={flightCredit.ticketNumber}
                        rowColumns={[
                          flightCredit.airline,
                          flightCredit.travelerName,
                          flightCredit.ticketNumber,
                          '$' + flightCredit.totalValue,
                          moment(flightCredit.expiryDate).format('MMM DD, YYYY'),
                          flightCredit.status,
                        ]}
                        extraAction={
                          <Tooltip title="Re-sync from Magnatech">
                            <span>
                              <IconButton
                                size="small"
                                disabled={
                                  !flightCredit.id ||
                                  (this.props.isSyncingFlightCredit &&
                                    this.state.syncingTicketId ===
                                      flightCredit.id)
                                }
                                onClick={() => this.handleSync(flightCredit.id)}
                              >
                                {this.props.isSyncingFlightCredit &&
                                this.state.syncingTicketId === flightCredit.id ? (
                                  <CircularProgress size={16} />
                                ) : (
                                  <SyncIcon fontSize="small" sx={{ color: '#1976d2' }} />
                                )}
                              </IconButton>
                            </span>
                          </Tooltip>
                        }
                        clickEdit={this.onClickEdit.bind(
                          this,
                          flightCredit.ticketNumber,
                          flightCredit.status,
                          flightCredit.id,
                        )}
                        showEdit={this.props.hasPermission}
                      />
                    );
                  })}
                </TableBody>
              </Table>
            </div>
          </div>
        </div>
        <UpdateFlightCreditPopup
          handleStatusSelect={this.handleStatusSelect}
          open={this.state.updatePopup}
          onClose={this.onClosePopup}
          update={this.handleUpdateStatus}
          ticketNumber={this.state.ticketNumber}
          status={this.state.status}
          id={this.state.id}
        />
      </Paper>
    );
  }
}

function mapStateToProps(state: ReduxState) {
  return {
    isLoading: state.traveler.isLoading,
    userLoading: state.user.isLoading,
    isSyncingFlightCredit: (state.user as any).isSyncingFlightCredit || false,
  };
}

// Wire up redux dispatch functions
function mapDispatchToProps(dispatch: Dispatch) {
  return bindActionCreators(
    {
      updateUserFlightCredit,
      syncUserFlightCredit,
    },
    dispatch,
  );
}

// Connect it to Redux
export default connectWithRouter(mapStateToProps, mapDispatchToProps)(FlightCredits);
